export const privacyTranslations = {
  title: "Privacy Policy",
  description: "Privacy policy and personal data protection for Leadsme. GDPR compliance, data management, cookies and user rights.",
  content: `This privacy policy aims to inform users of the Leadsme service about how their personal data is collected, used, and protected, in accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016 (the "GDPR").

1. Data Controller
The controller of personal data processing is:
- Name: DUHR Romain
- Status: Individual Entrepreneur (EI)
- Trade Name: Leadsme
- Contact Address: contact@leadsme.app
The postal address of the data controller is indicated in the legal notices on the website.

2. Personal Data Collected
In the context of using the service, the following categories of personal data may be processed:
- User account data
- Data related to prospects (leads)
- Project-related data
- File-related information (metadata)
- Files uploaded by the user
- Payment tracking data
- Technical references related to subscriptions
- Logging data and technical traces
- Contact data (email address)
- Content of emails you compose and send from Leadsme via connected Gmail
- Data related to optional Google integrations (Gmail, Google Calendar), when you choose to connect them (see section 11)
When you accept marketing cookies, browsing data may also be collected via the Meta Pixel (Meta), including pages viewed, actions taken on the site (e.g. sign-up, subscription), IP address and technical identifiers (_fbp, _fbc cookies).
Passwords are never stored in plain text. They are protected by appropriate measures, including a secure hashing mechanism managed by the authentication provider.

3. Collection Purposes
Personal data is processed for the following purposes:
- User account management and administration
- Prospecting and commercial follow-up management
- Project and deliverable management
- Hosting and management of files necessary for service operation
- Informative tracking of payments and subscriptions
- Sending transactional emails related to the service (authentication, security, notifications)
- Management and response to contact and support requests
- Service security and abuse prevention
- Service maintenance and improvement
- Anonymous audience measurement (Umami)
- Advertising performance measurement and conversion analysis (Meta Pixel), only with your consent
- Sending prospecting emails via your connected Gmail account, only when you explicitly trigger send from the Messages module
- Syncing Leadsme events to a "Leadsme" Google Calendar, when you connect Google Calendar
- Sending one email reminder on the day a lead follow-up is due, unless disabled in Settings, Preferences tab
Data processed in the context of the service is associated with the user's account and is not shared between users.

4. Legal Basis for Processing
Data processing is based on the following legal bases:
- Contract performance: for service provision, payment and subscription management, and for Gmail or Google Calendar features you choose to activate (see section 11)
- Legitimate interest: for service security, abuse prevention, technical maintenance and anonymous audience statistics (Umami)
- Consent: for marketing cookies and use of the Meta Pixel (advertising and conversion measurement), when you accept via the cookie manager

5. Data Retention Period
Personal data is retained for periods proportionate to the purposes pursued:
- Account data, prospect data, project data, as well as payment and subscription tracking data: during the user account duration
- Contact and support data: for the duration necessary to process the request
- Logging data and technical traces: up to 12 months
- Marketing cookies (Meta Pixel): according to Meta settings, generally up to 90 days from placement
- Cookie consent preferences: stored locally in your browser until modified or deleted
- Google OAuth tokens (Gmail, Google Calendar): while the integration remains connected; deleted on disconnect or account deletion
- Information about emails sent from Leadsme (send history): for the lifetime of the account, including after Gmail disconnect, unless deleted earlier by the user (clearing a lead's history) or on account deletion
- Technical log of scheduled transactional emails (follow-up reminders, demo reminder, etc.): retained to prevent duplicates; follow-up reminder rows are purged after about 90 days; erased with the account

6. Recipients and Subcontractors
Personal data may be transmitted to the following recipients and technical providers, some acting as subprocessors on behalf of the data controller:
- Supabase: database hosting, authentication, and technical logs
- AWS S3: storage of files uploaded by users
- AWS SES: sending transactional emails
- Stripe: payment and subscription processing
- Umami: anonymous audience statistics
- Meta (Meta Platforms Ireland Limited / Meta Platforms, Inc.): advertising measurement, conversion analysis and marketing cookies via the Meta Pixel, only with your consent
- Google Ireland Limited and Google LLC: technical providers enabling email sending via Gmail and sync with Google Calendar when you activate these integrations (see section 11)
The publisher may access data in a limited manner when necessary for proper service operation, user assistance, or compliance with legal obligations.

7. Transfers Outside the European Union
Some data may be transferred outside the European Union, particularly through the use of Stripe, Meta and Google providers.
These transfers are subject, depending on the situation, to the EU-US Data Privacy Framework, Standard Contractual Clauses approved by the European Commission, or any other applicable legal mechanism.

8. User Rights
In accordance with the GDPR, users have the following rights:
- Right of access to their personal data
- Right to rectification
- Right to erasure
- Right to object
- Right to restriction of processing
- Right to data portability
- Right to withdraw consent to marketing cookies at any time via the "Manage cookies" link in the page footer, without affecting the lawfulness of processing based on consent given before withdrawal
These rights may be exercised from Account Settings (data export and account deletion), or by contacting the data controller at the following address: contact@leadsme.app.
Deleting your account permanently erases account-related data (including leads, projects, files, templates, preferences, Google connections and Gmail send history) and cancels any active subscription. An optional leaving reason may be collected; when provided, it is sent by email to the operator to improve the service, and is not stored in a dedicated application database.

9. Cookies
Leadsme uses cookies and similar technologies to operate the site, measure audience, and, with your consent, improve advertising campaigns.

Strictly necessary cookies
These cookies are essential for the service to work and do not require your consent. They notably allow:
- authentication to your account;
- remembering certain preferences (e.g. language);
- the subscription and payment flow to work.
They are placed by Leadsme and its technical providers, including Supabase and Stripe.

Audience measurement (Umami)
Leadsme uses Umami to obtain anonymous site usage statistics. Umami does not place advertising cookies, does not use personal identifiers and does not require your consent.

Marketing cookies (Meta Pixel)
With your consent, Leadsme uses the Meta Pixel to:
- measure the effectiveness of advertising campaigns;
- analyze conversions (visit, sign-up, subscription);
- create advertising audiences on Meta platforms.
The Meta Pixel is only enabled if you accept marketing cookies. Relevant cookies (_fbp and _fbc) are retained according to Meta settings, generally up to 90 days.

Managing your preferences
On your first visit, you can accept, reject or customize marketing cookies. You can change your choice at any time via the "Manage cookies" link in the page footer. Your preferences are stored locally in your browser.

10. Data Security
The data controller implements reasonable technical and organizational measures to ensure personal data security, including:
- Secure hosting
- Restricted data access
- Tools compliant with data protection requirements
However, no data transmission or storage can be guaranteed as completely secure.

11. Google Integrations (Gmail, Google Calendar and Google Drive)

Leadsme offers optional Google integrations. They are only enabled if you choose to connect your Google account from Settings, Integrations tab.

11.1 Connection and consent
- Connection is voluntary and can be revoked at any time from Settings, Integrations tab (Disconnect).
- When connecting, Google displays the requested permissions. Leadsme only uses access strictly necessary for the features described below.
- OAuth tokens are stored server-side (Leadsme infrastructure / Supabase) and are never exposed to the browser or shared with other users.

11.2 Gmail (email sending)
If you connect Gmail, Leadsme uses the gmail.send permission only to send emails on your behalf when you explicitly click "Send" in the Messages module, after reviewing the recipient, subject, and message content.
Leadsme does not read, search, modify, or delete emails in your inbox, drafts, or labels.
Data involved: connected Gmail address; content, recipient and subject of messages you compose and send from Leadsme; technical identifiers returned by Gmail (message and thread IDs), retained to identify the send and offer a link to the conversation in Gmail.
This data is associated with your Leadsme account and the relevant lead.

11.3 Google Calendar (sync)
If you connect Google Calendar, Leadsme uses the calendar.app.created permission, which allows managing calendars created by the application. Leadsme limits its use to the secondary "Leadsme" calendar and events it creates or syncs there.
Data involved: identifiers and metadata of synced events (title, dates, type, link to a Leadsme lead or project).

11.4 Google Drive
If you connect Google Drive, Leadsme uses the drive.file permission, limited to files you create with the application or explicitly choose to share with it. Leadsme does not access your entire Drive.
Data involved: connected Google address; identifiers and metadata of linked or exported files (name, type, link), when a link or export feature is used.

11.5 Purpose and legal basis
This processing relies on contract performance when you request activation and use of the relevant Gmail, Google Calendar or Google Drive features.

11.6 Retention
- OAuth tokens are retained while the integration remains connected. They are deleted on disconnect or account deletion.
- Information about emails sent from Leadsme is retained for the lifetime of the account, including after Gmail disconnect, unless deleted earlier by the user (clearing a lead's history) or on account deletion.
- Google Calendar sync metadata is retained for the account lifetime; disconnecting Google Calendar removes active sync links without deleting your Leadsme events.
- Linked Drive file metadata is retained for the account lifetime or until the link / related project is deleted.

11.7 Transfers
When you use these integrations, Google Ireland Limited and Google LLC act as technical providers. Data may be processed in the United States, subject to the mechanisms described in section 7.

11.8 Limited use of Google user data
Leadsme's use and transfer to any other app of information received from Google APIs adheres to the Google API Services User Data Policy, including its Limited Use requirements.
Leadsme does not use data obtained via Google APIs for advertising, does not sell it, and does not allow human access to such data except with your explicit consent, for security reasons, to comply with a legal obligation, or when such access is strictly necessary to resolve an incident or provide support you have requested.

12. Language and Applicable Version
This privacy policy is written in French.
In case of divergence in interpretation with a translated version, the French version takes precedence.

13. Policy Modification
This privacy policy may be modified at any time to reflect changes in the service or regulations.
The version in force is that published on the website at the time of consultation.`,
  lastUpdate: "July 29, 2026",
} as const;
